import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface WikiSpec {
  label: string;
  value: string;
}

export interface WikiPros {
  text: string;
}

export interface WikiPerformance {
  cycleLife: number;
  weight: number;
  voltage: number;
  cost: number;
}

export interface WikiArticle {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  badge: string;
  badgeColor: string;
  icon: string;
  heroImage: string;
  summary: string;
  body: string;
  specs: WikiSpec[];
  pros: WikiPros[];
  cons: WikiPros[];
  useCases: string[];
  performance: WikiPerformance;
  published: boolean;
}

export interface WikiSettings {
  pageTitle: string;
  pageSubtitle: string;
  articles: WikiArticle[];
}

export const defaultWikiSettings: WikiSettings = {
  pageTitle: 'Battery Technology Guide',
  pageSubtitle: 'Everything you need to know about marine battery technologies — explained simply.',
  articles: [
    {
      id: 'lifepo4',
      slug: 'lifepo4',
      title: 'LiFePO4 Lithium',
      subtitle: 'The Future of Marine Power',
      badge: 'Premium',
      badgeColor: '#10B981',
      icon: 'ri-flashlight-line',
      heroImage: 'https://readdy.ai/api/search-image?query=modern%20lithium%20battery%20cell%20glowing%20blue%20green%20energy%20technology%20close%20up%20macro%20photography%20clean%20white%20background%20futuristic%20high%20tech%20battery%20pack%20with%20visible%20cells%20and%20terminals%20professional%20product%20photography&width=800&height=500&seq=wiki-lifepo4&orientation=landscape',
      summary: 'Lithium Iron Phosphate (LiFePO4) is the safest, most energy-dense battery chemistry available for marine applications. With 2000+ charge cycles and a 10-year design life, it\'s the premium choice for serious boaters.',
      body: 'LiFePO4 (Lithium Iron Phosphate) batteries represent the pinnacle of marine battery technology. Unlike traditional lead-acid batteries, LiFePO4 cells maintain a stable voltage throughout the discharge cycle, meaning your electronics and motors perform consistently from 100% to near 0% charge.\n\nThe chemistry is inherently stable — no thermal runaway risk, no toxic gases, and no memory effect. You can discharge to 80% depth of discharge (DoD) regularly without degrading the battery, compared to just 50% for AGM.\n\nWeight is another game-changer: a 100Ah LiFePO4 battery weighs roughly 13kg versus 28kg for an equivalent AGM. On a 35-foot sportfisher, replacing three AGM batteries with one Stingray lithium saves over 150 lbs — dramatically improving performance and fuel efficiency.',
      specs: [
        { label: 'Cycle Life', value: '2000+ cycles' },
        { label: 'Depth of Discharge', value: '80% DoD' },
        { label: 'Weight vs AGM', value: '50% lighter' },
        { label: 'Design Life', value: '10 years' },
        { label: 'Self-Discharge', value: '<3% / month' },
        { label: 'Warranty', value: '5 years' },
      ],
      pros: [
        { text: 'Longest cycle life — 2000+ charge cycles' },
        { text: 'Lightest weight per amp-hour' },
        { text: 'Stable voltage throughout discharge' },
        { text: 'No maintenance required' },
        { text: 'Safe chemistry — no thermal runaway' },
        { text: 'Built-in BMS protection' },
      ],
      cons: [
        { text: 'Higher upfront cost' },
        { text: 'Requires compatible charger' },
        { text: 'Not ideal for extreme cold below -20°C' },
      ],
      useCases: ['Offshore fishing boats', 'Sailing yachts', 'Electric trolling motors', 'House bank systems', 'Commercial vessels'],
      performance: { cycleLife: 95, weight: 30, voltage: 90, cost: 35 },
      published: true,
    },
    {
      id: 'agm',
      slug: 'agm',
      title: 'AGM',
      subtitle: 'Absorbed Glass Mat Technology',
      badge: 'Popular',
      badgeColor: '#0EA5E9',
      icon: 'ri-battery-charge-line',
      heroImage: 'https://readdy.ai/api/search-image?query=AGM%20absorbed%20glass%20mat%20battery%20marine%20boat%20battery%20black%20case%20professional%20product%20photography%20clean%20studio%20white%20background%20high%20quality%20detailed%20shot%20showing%20terminals%20and%20label&width=800&height=500&seq=wiki-agm&orientation=landscape',
      summary: 'AGM batteries are the most popular choice for marine use. Sealed, spill-proof, and maintenance-free, they offer excellent performance at a mid-range price point. Ideal for most recreational boaters.',
      body: 'Absorbed Glass Mat (AGM) batteries use a fiberglass mat separator to absorb and hold the electrolyte between the battery plates. This design makes them completely sealed and spill-proof — you can mount them in any orientation without worrying about acid leaks.\n\nAGM batteries excel at delivering high burst currents, making them ideal for engine starting. They also handle deep cycling better than traditional flooded lead-acid batteries, though not as well as lithium.\n\nThe technology is mature, reliable, and widely supported. Any standard marine charger will work with AGM batteries, and they perform well across a wide temperature range — from arctic conditions to tropical heat. For most recreational boaters, AGM represents the sweet spot of performance, reliability, and value.',
      specs: [
        { label: 'Cycle Life', value: '400-600 cycles' },
        { label: 'Depth of Discharge', value: '50% DoD' },
        { label: 'Design Life', value: '3-5 years' },
        { label: 'Self-Discharge', value: '1-3% / month' },
        { label: 'Temperature Range', value: '-40°C to +60°C' },
        { label: 'Warranty', value: '3 years' },
      ],
      pros: [
        { text: 'Sealed and spill-proof — mount anywhere' },
        { text: 'Excellent cold cranking performance' },
        { text: 'Wide temperature range' },
        { text: 'Compatible with all standard chargers' },
        { text: 'Mid-range price point' },
        { text: 'Proven, reliable technology' },
      ],
      cons: [
        { text: 'Heavier than lithium' },
        { text: 'Fewer cycles than LiFePO4' },
        { text: 'Sensitive to overcharging' },
      ],
      useCases: ['Recreational powerboats', 'Engine starting', 'Day boats', 'Fishing boats', 'Pontoon boats'],
      performance: { cycleLife: 40, weight: 60, voltage: 65, cost: 65 },
      published: true,
    },
    {
      id: 'gel',
      slug: 'gel',
      title: 'Gel',
      subtitle: 'Silica Gel Electrolyte Technology',
      badge: 'Specialist',
      badgeColor: '#8B5CF6',
      icon: 'ri-drop-line',
      heroImage: 'https://readdy.ai/api/search-image?query=gel%20battery%20marine%20technology%20silica%20electrolyte%20cross%20section%20diagram%20illustration%20clean%20professional%20technical%20product%20photography%20white%20background%20showing%20internal%20structure%20battery%20cells&width=800&height=500&seq=wiki-gel&orientation=landscape',
      summary: 'Gel batteries use a silica-based electrolyte that provides ultra-stable voltage output — perfect for sensitive marine electronics. They are the specialist choice for electronics-heavy vessels and solar applications.',
      body: 'Gel batteries replace the liquid electrolyte found in traditional batteries with a silica-based gel. This gel immobilizes the electrolyte, making the battery completely sealed, vibration-resistant, and capable of being mounted in virtually any position.\n\nThe key advantage of gel technology is its exceptionally stable and consistent voltage output. This makes gel batteries the preferred choice for vessels with sensitive electronics — chartplotters, radar systems, autopilots, and communication equipment all benefit from the clean, stable power delivery.\n\nGel batteries also excel in deep cycle applications and perform particularly well in hot climates, where their sealed construction prevents electrolyte evaporation. They are commonly used in solar charging systems on liveaboard vessels, where slow, steady charging and discharging is the norm.\n\nOne important note: gel batteries require a specific gel-compatible charger. Using a standard AGM charger can damage the battery by applying too high a charge voltage.',
      specs: [
        { label: 'Cycle Life', value: '500-800 cycles' },
        { label: 'Depth of Discharge', value: '50% DoD' },
        { label: 'Design Life', value: '4-6 years' },
        { label: 'Self-Discharge', value: '<1% / month' },
        { label: 'Voltage Stability', value: 'Excellent' },
        { label: 'Warranty', value: '3 years' },
      ],
      pros: [
        { text: 'Ultra-stable voltage — ideal for electronics' },
        { text: 'Lowest self-discharge rate' },
        { text: 'Excellent in hot climates' },
        { text: 'Vibration and shock resistant' },
        { text: 'Long deep cycle life' },
        { text: 'No gas emission — safe in enclosed spaces' },
      ],
      cons: [
        { text: 'Requires gel-specific charger' },
        { text: 'Sensitive to overcharging' },
        { text: 'Slower charge acceptance rate' },
      ],
      useCases: ['Electronics-heavy vessels', 'Liveaboard yachts', 'Solar charging systems', 'Sailboats', 'Enclosed battery compartments'],
      performance: { cycleLife: 50, weight: 55, voltage: 85, cost: 60 },
      published: true,
    },
  ],
};

interface WikiContextType {
  settings: WikiSettings;
  updateSettings: (s: WikiSettings) => void;
  resetSettings: () => void;
}

const WikiContext = createContext<WikiContextType | null>(null);

const STORAGE_KEY = 'stingray_wiki_settings';

function clampPerformanceValue(value: unknown, fallback: number) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(0, Math.min(100, Math.round(parsed)));
}

function normalizeArticle(article: Partial<WikiArticle> | undefined, fallback: WikiArticle): WikiArticle {
  return {
    ...fallback,
    ...article,
    specs: Array.isArray(article?.specs) ? article.specs : fallback.specs,
    pros: Array.isArray(article?.pros) ? article.pros : fallback.pros,
    cons: Array.isArray(article?.cons) ? article.cons : fallback.cons,
    useCases: Array.isArray(article?.useCases) ? article.useCases : fallback.useCases,
    performance: {
      cycleLife: clampPerformanceValue(article?.performance?.cycleLife, fallback.performance.cycleLife),
      weight: clampPerformanceValue(article?.performance?.weight, fallback.performance.weight),
      voltage: clampPerformanceValue(article?.performance?.voltage, fallback.performance.voltage),
      cost: clampPerformanceValue(article?.performance?.cost, fallback.performance.cost),
    },
    published: article?.published ?? fallback.published,
  };
}

function normalizeSettings(raw: Partial<WikiSettings> | null | undefined): WikiSettings {
  const next = raw ?? {};
  const articles = Array.isArray(next.articles)
    ? next.articles.map((article, index) => normalizeArticle(article, defaultWikiSettings.articles[index] ?? defaultWikiSettings.articles[0]))
    : defaultWikiSettings.articles;

  return {
    pageTitle: next.pageTitle || defaultWikiSettings.pageTitle,
    pageSubtitle: next.pageSubtitle || defaultWikiSettings.pageSubtitle,
    articles,
  };
}

export function WikiProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<WikiSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? normalizeSettings(JSON.parse(stored) as Partial<WikiSettings>) : defaultWikiSettings;
    } catch {
      return defaultWikiSettings;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeSettings(settings)));
  }, [settings]);

  const updateSettings = (s: WikiSettings) => setSettings(normalizeSettings(s));
  const resetSettings = () => setSettings(defaultWikiSettings);

  return (
    <WikiContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </WikiContext.Provider>
  );
}

export function useWiki() {
  const ctx = useContext(WikiContext);
  if (!ctx) throw new Error('useWiki must be used within WikiProvider');
  return ctx;
}
